/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import * as React from "react";
import styled from "styled-components";
import {
  Plan,
  PlanTitleContainer,
  PlanSubtitleContainer,
  Product,
  Products,
  ProductTitle,
} from "components/Plans/Plan";
import iconNoBulk from "./images/no-bulk.svg";
import iconNoStrength from "./images/no-strength.svg";
import iconMaxDuration from "./images/max-duration.svg";
import iconMicCheck from "./images/mic-check-dark.svg";
import iconAudioOnly from "./images/audio-only.svg";
import iconClock from "./images/clock.svg";
import iconSpeakersDark from "images/Studio/speakers-icon-dark-features-page-size.svg";
import { useCurrentUser } from "actions/CurrentUserActions";
import { ProductFeature } from "components/Plans/ProductFeature";
import { useCallback } from "react";
import { IconVideo } from "components/Plans/IconComponent";
import { IconMode } from "components/Plans/IconComponent";
import "stylesheets/spectrum.scss";

const Link = styled.a.attrs({
  className: "spectrum-Link",
})<{ displayType?: string }>`
  margin-top: 16px;
  display: ${(props): string => props.displayType || "block"};
`;

const Account = styled.div`
  margin-top: 16px;
`;

const SignInButton: React.FC = () => {
  const signIn = useCallback(() => {
    window.adobeIMS?.signIn();
  }, []);
  return (
    <div>
      <sp-button onClick={signIn} size="xl" treatment="outline" static="black">
        {I18n.t("plans.free.sign_up")}
      </sp-button>
      <Account>
        {`${I18n.t("plans.free.already_signed_up")} `}
        <Link displayType={"inline"} href="#" onClick={signIn}>
          {I18n.t("login.sign_in")}
        </Link>
      </Account>
    </div>
  );
};

const HasFreeActions: React.FC = () => {
  return (
    <div>
      <sp-button size="xl" disabled treatment="outline" static="black">
        {I18n.t("plans.free.button")}
      </sp-button>
      <Link href={process.env.ACCOUNTS_PAGE}>
        {I18n.t("plans.free.your_account")}
      </Link>
    </div>
  );
};

const AccountActions: React.FC = () => {
  const { currentUser } = useCurrentUser();

  if (!currentUser) {
    return <SignInButton />;
  }

  if (currentUser.hasPremiumAccess) {
    return <></>;
  }

  return <HasFreeActions />;
};

export const FreePlan: React.FC = () => {
  return (
    <Plan data-testid={"plan-free"}>
      <PlanTitleContainer>{I18n.t("plans.free.title")}</PlanTitleContainer>
      <PlanSubtitleContainer>
        {I18n.t("plans.free.subtitle")}
      </PlanSubtitleContainer>
      <Products>
        <Product>
          <ProductTitle>
            {I18n.t("plans.free.enhance_speech.title")}
          </ProductTitle>
          <ProductFeature
            image={iconAudioOnly}
            description={I18n.t("plans.free.enhance_speech.audio_only")}
          />
          <ProductFeature
            image={iconNoBulk}
            description={I18n.t("plans.free.enhance_speech.no_bulk")}
          />
          <ProductFeature
            image={iconNoStrength}
            description={I18n.t("plans.free.enhance_speech.no_strength")}
          />
          <ProductFeature
            image={iconMaxDuration}
            description={I18n.t("plans.free.enhance_speech.max_duration")}
          />
        </Product>
        <Product>
          <ProductTitle>
            {I18n.t("plans.free.podcast_studio.title")}
          </ProductTitle>
          <ProductFeature
            image={iconClock}
            description={I18n.t("plans.free.podcast_studio.download")}
          />
          <ProductFeature
            image={iconSpeakersDark}
            description={I18n.t(
              "plans.free.podcast_studio.download_original_recordings"
            )}
          />
          <ProductFeature
            image={IconVideo}
            mode={IconMode.dark}
            description={I18n.t(
              "plans.free.podcast_studio.podcast_branded_audiograms"
            )}
          />
        </Product>
        <Product>
          <ProductTitle>{I18n.t("plans.free.mic_check.title")}</ProductTitle>
          <ProductFeature
            image={iconMicCheck}
            description={`${I18n.t("plans.premium.mic_check.analyze")} ${I18n.t(
              "plans.premium.mic_check.description"
            )}`}
          />
        </Product>
      </Products>
      <AccountActions />
    </Plan>
  );
};
